import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { FEATURE_FLAG_KEYS } from 'src/config/featureFlags';
import { isTrafficViewerRole } from 'src/constants/roles.constants';
import { DEEP_LINK_PARAMS } from 'src/routing/deep-links/deepLinkParams';
import { isFeatureEnabledSelector } from 'src/store/feature-flags/feature-flags.selectors';
import { isDeepLinkLocationPending } from 'src/store/user/user.operations';
import { userAgencyIdSelector, userRoleSelector } from 'src/store/user/user.selectors';
import { selectedJurisdictionSelector, userAgenciesSelector } from 'src/store/user-info/user-info.selectors';

/**
 * Keeps the shared location params (agency=, and jur= for traffic viewers) in
 * every authenticated page's URL, so any copied URL names its own agency
 * (docs/deep-links-system-design.md §5). Router-state sync only: it merges
 * into the current query string with `replace`, leaving feature params alone,
 * and never changes the agency itself.
 */
const DeepLinkLocationSync = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const enabled = useSelector((state) => isFeatureEnabledSelector(state, FEATURE_FLAG_KEYS.DEEP_LINKS));
    const agencyId = useSelector(userAgencyIdSelector);
    const isTrafficViewer = isTrafficViewerRole(useSelector(userRoleSelector));
    const selectedJurisdictionId = useSelector(selectedJurisdictionSelector);
    const { list } = useSelector(userAgenciesSelector);
    const jurisdictionId = isTrafficViewer ? selectedJurisdictionId : null;

    useEffect(() => {
        if (!enabled || !agencyId
            || isDeepLinkLocationPending(location.search, list, { agencyId, jurisdictionId })) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const wanted = [
            [DEEP_LINK_PARAMS.AGENCY, agencyId],
            [DEEP_LINK_PARAMS.JURISDICTION, jurisdictionId],
        ];
        let changed = false;
        wanted.forEach(([name, value]) => {
            const next = value == null || value === '' ? null : String(value);
            if (params.get(name) === next) {
                return;
            }
            changed = true;
            if (next === null) {
                params.delete(name);
            } else {
                params.set(name, next);
            }
        });

        if (changed) {
            navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
        }
    }, [enabled, agencyId, jurisdictionId, list, location.pathname, location.search, navigate]);

    return null;
};

export default DeepLinkLocationSync;
